import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.array.iterator.js";
import "core-js/modules/es.array.join.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.promise.js";
import "core-js/modules/es.string.iterator.js";
import "core-js/modules/es.string.small.js";
import "core-js/modules/web.dom-collections.iterator.js";
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'components/Button';
import config from 'utils/config';
import { alpha, isLight } from 'utils/ui/colors';
import getBasicDate from 'utils/dates/getBasicDate';
import downloadBlob from 'utils/file/downloadBlob';
import sanitizeForFileName from 'utils/file/sanitizeForFileName';
import getFullUserName from 'utils/getFullUserName';
import { useIsHelixAdminView } from 'utils/helix';
import getMeetingAnalysisTitle from '../MeetingReportDocument/getMeetingAnalysisTitle';
import { jsx as _jsx } from "react/jsx-runtime";
function getReportFileName(meeting, userName, t) {
  var safeAnalysisTitle = sanitizeForFileName(getMeetingAnalysisTitle(userName, t));
  var safeMeetingTitle = meeting.title ? sanitizeForFileName(meeting.title) : '';
  // Local time, not toISOString() (UTC) - can land on the wrong day for
  // users outside UTC otherwise.
  var datePart = meeting.startTime ? getBasicDate(new Date(meeting.startTime)) : '';
  return "".concat([safeAnalysisTitle, safeMeetingTitle, datePart].filter(Boolean).join(' - '), ".pdf");
}

// The PDF page keeps react-pdf's white background regardless of a hub's own
// light/dark web theme (a fixed, print-safe background). A dark hub's theme
// colour can be light (meant for a dark page) and unreadable/washed-out
// against that white background, so it's only used when it has safe
// contrast - otherwise this falls back to undefined, letting
// MeetingReportDocument's own defaults apply.
function getSafeColor(themeColor) {
  return themeColor && !isLight(themeColor) ? themeColor : undefined;
}
function DownloadReportButton(_ref) {
  var _config$styles, _config$styles2, _config$styles3, _config$styles4, _config$styles5, _config$styles6, _config$styles7;
  var meeting = _ref.meeting,
    skills = _ref.skills,
    user = _ref.user;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  // The PDF renders outside the app's React tree, so it's handed the view rather than reading it
  var isAdminView = useIsHelixAdminView();
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isGenerating = _useState2[0],
    setIsGenerating = _useState2[1];

  // Matches how UserPageHeader itself derives a display name, including
  // its email fallback.
  var userName = user ? getFullUserName({
    first_name: user.firstName,
    last_name: user.lastName
  }) || user.email : undefined;

  // Passed down as plain props - MeetingReportDocument runs outside the
  // app's React tree, so it can't read config/context itself.
  var logoUrl = (_config$styles = config.styles) === null || _config$styles === void 0 || (_config$styles = _config$styles.globals) === null || _config$styles === void 0 || (_config$styles = _config$styles.logo) === null || _config$styles === void 0 ? void 0 : _config$styles.emailUrl;
  var textColor = getSafeColor((_config$styles2 = config.styles) === null || _config$styles2 === void 0 || (_config$styles2 = _config$styles2.sections) === null || _config$styles2 === void 0 || (_config$styles2 = _config$styles2.page) === null || _config$styles2 === void 0 ? void 0 : _config$styles2.text);
  var accentColor = getSafeColor((_config$styles3 = config.styles) === null || _config$styles3 === void 0 || (_config$styles3 = _config$styles3.sections) === null || _config$styles3 === void 0 || (_config$styles3 = _config$styles3.page) === null || _config$styles3 === void 0 ? void 0 : _config$styles3.links) || getSafeColor((_config$styles4 = config.styles) === null || _config$styles4 === void 0 || (_config$styles4 = _config$styles4.globals) === null || _config$styles4 === void 0 || (_config$styles4 = _config$styles4.buttons) === null || _config$styles4 === void 0 || (_config$styles4 = _config$styles4.primary) === null || _config$styles4 === void 0 ? void 0 : _config$styles4.background);
  var staticOrigin = config.STATIC_ORIGIN;
  // Matches the hub's PercentageDisc "shady" background (a tinted version
  // of the page's shade colour).
  var shadeColor = (_config$styles5 = config.styles) === null || _config$styles5 === void 0 || (_config$styles5 = _config$styles5.sections) === null || _config$styles5 === void 0 || (_config$styles5 = _config$styles5.page) === null || _config$styles5 === void 0 ? void 0 : _config$styles5.shade;
  var shadeStrength = (_config$styles6 = config.styles) === null || _config$styles6 === void 0 || (_config$styles6 = _config$styles6.sections) === null || _config$styles6 === void 0 || (_config$styles6 = _config$styles6.page) === null || _config$styles6 === void 0 ? void 0 : _config$styles6.shadeStrength;
  var circleBackgroundColor = shadeColor && typeof shadeStrength === 'number' ? alpha(shadeColor, shadeStrength) : undefined;
  var borderRadiusSmall = (_config$styles7 = config.styles) === null || _config$styles7 === void 0 || (_config$styles7 = _config$styles7.globals) === null || _config$styles7 === void 0 || (_config$styles7 = _config$styles7.borderRadius) === null || _config$styles7 === void 0 ? void 0 : _config$styles7.small;
  function handleClick() {
    return _handleClick.apply(this, arguments);
  }
  function _handleClick() {
    _handleClick = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var _yield$Promise$all, _yield$Promise$all2, pdf, MeetingReportDocument, blob;
      return _regeneratorRuntime.wrap(function (_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            setIsGenerating(true);
            _context.prev = 1;
            _context.next = 2;
            return Promise.all([import(/* webpackChunkName: "react-pdf" */'@react-pdf/renderer'), import(/* webpackChunkName: "react-pdf" */'../MeetingReportDocument')]);
          case 2:
            _yield$Promise$all = _context.sent;
            _yield$Promise$all2 = _slicedToArray(_yield$Promise$all, 2);
            pdf = _yield$Promise$all2[0].pdf;
            MeetingReportDocument = _yield$Promise$all2[1].default;
            _context.next = 3;
            return pdf(/*#__PURE__*/_jsx(MeetingReportDocument, {
              meeting: meeting,
              skills: skills,
              userName: userName,
              t: t,
              logoUrl: logoUrl,
              textColor: textColor,
              accentColor: accentColor,
              staticOrigin: staticOrigin,
              circleBackgroundColor: circleBackgroundColor,
              borderRadiusSmall: borderRadiusSmall,
              isAdminView: isAdminView
            })).toBlob();
          case 3:
            blob = _context.sent;
            downloadBlob(blob, getReportFileName(meeting, userName, t));
          case 4:
            _context.prev = 4;
            setIsGenerating(false);
            return _context.finish(4);
          case 5:
          case "end":
            return _context.stop();
        }
      }, _callee, null, [[1,, 4, 5]]);
    }));
    return _handleClick.apply(this, arguments);
  }
  return /*#__PURE__*/_jsx(Button, {
    variant: "transparent",
    size: "small",
    icon: "download",
    isLoading: isGenerating,
    onClick: handleClick,
    children: t('actions_download_as_pdf')
  });
}
export default DownloadReportButton;